/**
 * Visitor history client — fetches the transcript of the visitor's most
 * recent reusable conversation so the widget can repopulate its UI on
 * refresh. Called once, right after handshake, before mounting the panel.
 *
 * Endpoint (see `mirlo-messages-service` PR #730):
 *   GET /api/v1/webchat/widgets/:widgetId/visitors/:visitorId/messages
 *   Authorization: Bearer <sessionToken from handshake>
 *
 * A `null` conversation_id means the backend has no active conversation
 * inside the 24h reuse window — the widget must mount empty (Intercom-
 * style: each fresh visit is a new session, even though the visitor_id
 * is preserved for dashboard tracking).
 *
 * The widget MUST treat any failure here as "no history": handshake
 * already succeeded, the chat should work — we just don't rehydrate.
 * Callers are expected to swallow errors from this module.
 */

import type { VisitorHistoryResponse } from './api-types';

export class HistoryFetchError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`History fetch failed (HTTP ${status}): ${code}`);
    this.name = 'HistoryFetchError';
  }
}

export async function fetchVisitorHistory(
  apiBaseUrl: string,
  widgetId: string,
  visitorId: string,
  sessionToken: string,
): Promise<VisitorHistoryResponse> {
  const url = `${apiBaseUrl}/api/v1/webchat/widgets/${encodeURIComponent(
    widgetId,
  )}/visitors/${encodeURIComponent(visitorId)}/messages`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` },
    });
  } catch (err) {
    throw new HistoryFetchError(0, (err as Error).message || 'network_error');
  }

  if (!response.ok) {
    let code = 'history_failed';
    try {
      const body = (await response.json()) as { message?: string };
      if (typeof body.message === 'string') code = body.message;
    } catch {
      // body wasn't JSON
    }
    throw new HistoryFetchError(response.status, code);
  }

  return (await response.json()) as VisitorHistoryResponse;
}
